import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { axiosClient } from "@/lib/axios";
import { HttpError } from "@/types/error";
import { PublicUser, WithPagination } from "@classview/shared-types";
import {
  UseQueryOptions,
  keepPreviousData,
  useQuery,
} from "@tanstack/react-query";

export const getUsers = async ({
  organisationId,
  pageSize = 10,
  pageIndex = 0,
  search,
}: {
  organisationId: string;
  pageSize?: number;
  pageIndex?: number;
  search?: string;
}): Promise<WithPagination<PublicUser>> => {
  const offset = pageIndex * pageSize;
  const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
  const response = await axiosClient.get(
    `/${organisationId}/users?limit=${pageSize}&offset=${offset}${searchParam}`,
  );
  return response.data;
};

export const getUsersAdmin = async ({
  organisationId,
  pageSize = 10,
  pageIndex = 0,
  search,
}: {
  organisationId?: string;
  pageSize?: number;
  pageIndex?: number;
  search?: string;
}): Promise<WithPagination<PublicUser>> => {
  const offset = pageIndex * pageSize;
  const response = await axiosClient.get(`/users`, {
    params: {
      limit: pageSize,
      offset,
      ...(organisationId && { orgId: organisationId }),
      ...(search && { search: search }),
    },
  });
  return response.data;
};

export function useGetUsers(
  {
    pageSize,
    pageIndex,
    search,
  }: { pageSize?: number; pageIndex?: number; search?: string | null },
  queryOptions?: Partial<
    UseQueryOptions<WithPagination<PublicUser>, HttpError>
  >,
) {
  const { organisation } = useOrganisation();
  return useQuery({
    queryKey: ["users", organisation.id, pageIndex, pageSize, search],
    queryFn: () =>
      getUsers({
        organisationId: organisation.id,
        pageSize,
        pageIndex,
        search: search ?? undefined,
      }),
    placeholderData: keepPreviousData,
    ...queryOptions,
  });
}

export function useGetUsersAdmin(
  {
    pageSize,
    pageIndex,
    search,
    organisationId,
  }: {
    organisationId?: string;
    pageSize?: number;
    pageIndex?: number;
    search?: string | null;
  },
  queryOptions?: Partial<
    UseQueryOptions<WithPagination<PublicUser>, HttpError>
  >,
) {
  return useQuery({
    queryKey: ["users", organisationId, pageIndex, pageSize, search],
    queryFn: () =>
      getUsersAdmin({
        organisationId,
        pageSize,
        pageIndex,
        search: search ?? undefined,
      }),
    placeholderData: keepPreviousData,
    ...queryOptions,
  });
}
